import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Card, Tooltip } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { ApplicationJsonItemResponse } from "@applications/models/ApplicationJsonModel";
import { useGetAssociatedApplications } from "@applications/services/ApplicationService";
import InviteApplicationUserButton from "@users/applicationuser/components/InviteApplicationUserButton";

import ApplicationUsers from "./ApplicationUsers";

const OrganizationApplications: FunctionComponent = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.applications"
  });

  const { applications, isLoading, error } = useGetAssociatedApplications();

  if (isLoading) return <LoadingComponent message={t("load.message")} />;
  if (error) return <ErrorComponent message={t("load.error.message")} />;

  return (
    <div data-testid="organizationApplications">
      <div className="flex justify-end">
        <InviteApplicationUserButton />
      </div>

      {applications?.map((appResponse: ApplicationJsonItemResponse) => {
        const application = appResponse.data;
        const appError = appResponse.error;

        let RuntimeCardComponent: null | ReactNode = null;

        let textValue = application.name;

        if (application.appEnv) {
          textValue += ` (${application.appEnv})`;
        }

        if (appError) {
          RuntimeCardComponent = (
            <Card
              data-testid={`organization-application-card-${application.id}`}
              className="border-2 border-red-500"
            >
              <Tooltip message={appError} placement="bottom-left">
                {textValue}
              </Tooltip>
            </Card>
          );
        } else {
          RuntimeCardComponent = (
            <Card
              expandableCardContent={
                <div className="rounded-md bg-white p-4">
                  <ApplicationUsers application={application} />
                </div>
              }
              data-testid={`organization-application-card-${application.id}`}
              className="min-w-max"
            >
              <span>{textValue}</span>
            </Card>
          );
        }

        return (
          <div
            className="mb-4 pt-10"
            key={`application-item-organization-panels-${application.id}`}
            data-testid={`application-item-organization-panels-${application.id}`}
          >
            {RuntimeCardComponent}
          </div>
        );
      })}
    </div>
  );
};

export default OrganizationApplications;
